import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "trainingId",
    "trainingSessionId",
    "cost",
    "companyKindWarning",
    "companyKindWarningContent",
    "urssafWarning",
    "urssafWarningContent",
    "networkAnimatorSelect",
    "trainersDetails",
    "generalInformationDetails",
    "timetablesDetails",
    "costDetails",
    "programDetails",
    "financementModeKindInput",
    "financementModeFifPlKindInput",
    "financementModeFifPlKindInputContainer",
    "financementModeAmountInput",
    "financementModeField",
    "financementModeFieldsContainer",
    "financementModesSection",
    "invoiceFirstName",
    "invoiceLastName",
    "invoiceAddress",
    "invoiceEmail",
    "nestedOutOfFifPlInvoicePaymentInstallmentsFields",
    "submitButton"
  ]

  static values = {
    findOrCreateTraineeElementId: String,
    trainingId: String,
    trainingSessionId: String,
    currentUserProfileNetworkAnimatorId: String
  }

  connect () {
    this.financementModeKindInputTargets.forEach(target => {
      if (target.value === 'out_of_fif_pl') {
        this._showPaymentInstallmentsFieldsForKindInput(target)
      }
    })

    this._syncFinancementModesForCurrentCost()
    this._updateSubmitButton()

    if (this.hasTrainingIdTarget && this.trainingIdTarget.value) {
      this.updateTrainingRelatedFields()
    }
  }

  updateCostRelatedFields () {
    this._toggleFinancementModesSection(!this._isZeroCost())
    this._updateSubmitButton()
  }

  updateTrainingRelatedFields () {
    this._updateSubmitButton()
    if (!this.hasTrainingIdTarget) return

    const trainingId = this.trainingIdTarget.value

    if (!trainingId) return

    fetch(`/api/v1/trainings/${trainingId}`)
      .then(response => response.json())
      .then(data => {
        this._updateCostFieldValue(data)
        this._updateFinancementModesFields(data)
        this._updateTrainingSessionSelectFieldOptions(data)
        this._updateTrainersDetails(data)
        this._updateGeneralInformationDetails(data)
        this._updateCostDetails(data)
        this._updateProgramDetails(data)
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        this._updateSubmitButton()
      })
  }

  updateTraineeRelatedFields () {
    this._updateSubmitButton()

    const findOrCreateTraineeElement = document.getElementById(this.findOrCreateTraineeElementIdValue)
    const traineeId = findOrCreateTraineeElement?.querySelector("select[name='enrollment[profiles_trainee_id]']")?.value

    if (!traineeId) return

    this.companyKindWarningTarget.classList.remove("d-none")
    this.companyKindWarningContentTarget.innerHTML = ""
    this.urssafWarningTarget.classList.remove("d-none")
    this.urssafWarningContentTarget.innerHTML = ""

    fetch(`/api/v1/profiles/trainees/${traineeId}`)
      .then(response => response.json())
      .then(data => {
        this._updateCompanyKindWarningContent(data)
        this._updateUrssafWarningContent(data)
        this._updateNetworkAnimatorSelect(data)
        this._updateInvoiceFields(data)
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        this._updateSubmitButton()
      })
  }

  updateTrainingSessionRelatedFields () {
    this._updateSubmitButton()
    if (!this.hasTrainingSessionIdTarget) return

    this._syncIndividualFifPlKindOption()

    const trainingSessionId = this.trainingSessionIdTarget.value

    if (!trainingSessionId) return

    fetch(`/api/v1/training_sessions/${trainingSessionId}`)
      .then(response => response.json())
      .then(data => {
        this._updatePaymentInstallmentsDueDate(data)
        this._updateGeneralInformationDetails(data)
        this._updateTimetablesDetails(data)
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        this._updateSubmitButton()
      })
  }

  _updateCostFieldValue (data) {
    this.costTarget.value = data.data.attributes.base_price
  }

  _updateFinancementModesFields (data) {
    const cost = this._parsedCost()
    const basePrice = Number.isNaN(cost) ? data.data.attributes.base_price : cost

    if (basePrice === 0) {
      this._toggleFinancementModesSection(false)
      return
    }

    this._toggleFinancementModesSection(true)
    this._clearFinancementModeFields()
    this._populateFinancementModesFromAttributes({
      base_price: basePrice,
      non_eligible_amount: data.data.attributes.non_eligible_amount
    })
  }

  _populateFinancementModesFromAttributes ({ base_price: basePrice, non_eligible_amount: nonEligibleAmount }) {
    const financementModeAddButton = this._financementModeAddButton()
    if (!financementModeAddButton) return

    const cappedNonEligibleAmount = Math.min(nonEligibleAmount || 0, basePrice)

    if (cappedNonEligibleAmount > 0) {
      financementModeAddButton.click()
      this.financementModeKindInputTargets[0].value = 'undefined'
      this.financementModeAmountInputTargets[0].value = basePrice - cappedNonEligibleAmount
      this.financementModeFifPlKindInputContainerTargets[0].classList.add('d-none')

      financementModeAddButton.click()
      this.financementModeKindInputTargets[1].value = 'out_of_fif_pl'
      this.financementModeAmountInputTargets[1].value = cappedNonEligibleAmount
      this.financementModeFifPlKindInputContainerTargets[1].classList.add('d-none')
      const paymentInstallmentsFields = this._showPaymentInstallmentsFieldsForKindInput(this.financementModeKindInputTargets[1])
      if (!paymentInstallmentsFields) return

      const paymentInstallmentsAddButton = paymentInstallmentsFields.querySelector("[data-action=\"enrollments--nested-form-payment-installments#add\"]")
      if (!paymentInstallmentsAddButton) return

      setTimeout(() => {
        paymentInstallmentsAddButton.click()
        const amountInput = paymentInstallmentsFields.querySelector(".nested-form__item [data-input-kind=\"amount\"]")
        amountInput.value = cappedNonEligibleAmount
        const dueDateInput = paymentInstallmentsFields.querySelector(".nested-form__item [data-input-kind=\"due_date\"]")
        dueDateInput.value = this._trainingSessionStartDate()
      }, 50) // To let Stimulus some time to initialize the controller
    } else {
      financementModeAddButton.click()
      this.financementModeKindInputTargets[0].value = 'undefined'
      this.financementModeAmountInputTargets[0].value = basePrice
      this.financementModeFifPlKindInputContainerTargets[0].classList.add('d-none')
    }
  }

  _clearFinancementModeFields () {
    this.financementModeFieldTargets.forEach(target => target.remove())
  }

  _toggleFinancementModesSection (visible) {
    if (this.hasFinancementModesSectionTarget) {
      this.financementModesSectionTarget.classList.toggle('d-none', !visible)
    }
  }

  _syncFinancementModesForCurrentCost () {
    if (!this.hasCostTarget) return

    this._toggleFinancementModesSection(!this._isZeroCost())
  }

  _parsedCost () {
    if (!this.hasCostTarget) return Number.NaN

    return parseFloat(this.costTarget.value)
  }

  _isZeroCost (cost = this._parsedCost()) {
    return !Number.isNaN(cost) && cost === 0
  }

  _financementModeAddButton () {
    return this.financementModeFieldsContainerTarget?.querySelector('[data-action="enrollments--nested-form-financement-modes#add"]')
  }

  _trainingSessionStartDate () {
    if (this.hasTrainingSessionIdTarget && this.trainingSessionIdTarget.value) {
      const selectedOption = this.trainingSessionIdTarget.selectedOptions[0]
      return selectedOption?.dataset?.startDate
    }

    return this.trainingSessionStartDateValue
  }

  _updateTrainingSessionSelectFieldOptions (data) {
    const trainingSessions = JSON.parse(data.data.attributes.coming_or_administrative_training_sessions).data
    const select = this.trainingSessionIdTarget
    const previousValue = select.value
    const prefilledId = this._prefilledTrainingSessionId()
    const preservedOption = prefilledId
      ? select.querySelector(`option[value="${prefilledId}"]`)
      : null
    const clonedOption = preservedOption ? preservedOption.cloneNode(true) : null
    const includeBlankOption = select.querySelector('option[value=""]')?.cloneNode(true)

    select.innerHTML = ''
    if (includeBlankOption) select.appendChild(includeBlankOption)

    trainingSessions.forEach(trainingSession => {
      const option = document.createElement('option')
      option.value = trainingSession.id
      option.textContent = trainingSession.attributes.long_title_with_enrollments
      option.disabled = trainingSession.attributes.full
      option.dataset.administrative = trainingSession.attributes.is_administrative ? "true" : "false"
      select.appendChild(option)
    })

    if (prefilledId && !this._selectHasOption(select, prefilledId) && clonedOption) {
      clonedOption.disabled = false
      select.appendChild(clonedOption)
    }

    const valueToSelect = [prefilledId, previousValue].find((value) => this._selectHasOption(select, value))
    if (valueToSelect) {
      const alreadySelected = select.value === String(valueToSelect)
      select.value = String(valueToSelect)
      if (!alreadySelected && String(valueToSelect) === String(prefilledId)) {
        select.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    this._syncIndividualFifPlKindOption()
  }

  _syncIndividualFifPlKindOption () {
    if (!this.hasTrainingSessionIdTarget) return

    const selectedOption = this.trainingSessionIdTarget.selectedOptions[0]
    const administrative = selectedOption?.dataset.administrative === "true"
    this._setIndividualFifPlKindOptionDisabled(!administrative)
  }

  _setIndividualFifPlKindOptionDisabled (disabled) {
    this.element.querySelectorAll("[data-enrollments--form-target~='financementModeFifPlKindInput'] option[value='individual']").forEach((option) => {
      const select = option.closest("select")
      if (!select || select.disabled) return

      option.disabled = disabled
      if (disabled && select.value === "individual") select.value = ""
    })

    this._setIndividualFifPlKindOptionDisabledInTemplate(disabled)
  }

  _setIndividualFifPlKindOptionDisabledInTemplate (disabled) {
    const template = this.element.querySelector("[data-enrollments--nested-form-financement-modes-target='template']")
    if (!template) return

    template.textContent = template.textContent.replace(/<option\b[^>]*\bvalue="individual"[^>]*>/g, (optionTag) => {
      const withoutDisabled = optionTag.replace(/\sdisabled(?:="[^"]*"|='[^']*'|=[^\s>]*)?/g, "")
      if (!disabled) return withoutDisabled

      return withoutDisabled.replace("<option", "<option disabled=\"disabled\"")
    })
  }

  _prefilledTrainingSessionId () {
    if (!this.trainingSessionIdValue) return null
    if (this.trainingIdValue && this.hasTrainingIdTarget && this.trainingIdTarget.value !== String(this.trainingIdValue)) {
      return null
    }

    return String(this.trainingSessionIdValue)
  }

  _selectHasOption (select, value) {
    if (!value) return false

    return Array.from(select.options).some(option => option.value === String(value))
  }

  _updateTrainersDetails (data) {
    this.trainersDetailsTarget.innerHTML = data.data.attributes.trainers_full_name
  }

  _updatePaymentInstallmentsDueDate (data) {
    this.nestedOutOfFifPlInvoicePaymentInstallmentsFieldsTargets.forEach(target => {
      const dueDateInput = target.querySelector(".nested-form__item [data-input-kind=\"due_date\"]")
      if (dueDateInput) {
        dueDateInput.value = data.data.attributes.start_date
        dueDateInput._flatpickr?.setDate(data.data.attributes.start_date)
      }
    })
  }

  _showPaymentInstallmentsFieldsForKindInput (kindInput) {
    const target = this._paymentInstallmentsFieldsForKindInput(kindInput)
    if (!target) return

    target.classList.remove('d-none')
    return target
  }

  _paymentInstallmentsFieldsForKindInput (kindInput) {
    const wrapper = kindInput.closest(".nested-form-wrapper")
    const fields = Array.from(wrapper?.querySelectorAll("[data-enrollments--form-target~='nestedOutOfFifPlInvoicePaymentInstallmentsFields']") || [])

    return fields.find((field) => !field.classList.contains("d-none")) || fields[fields.length - 1]
  }

  _updateGeneralInformationDetails (data) {
    this.generalInformationDetailsTarget.innerHTML = this._decodeHtml(data.data.attributes.general_information_details_html)
  }

  _updateTimetablesDetails (data) {
    const timetables = data.data.attributes.timetables || []
    this.timetablesDetailsTarget.innerHTML = timetables.map(timetable => `<p>${timetable}</p>`).join('')
  }

  _updateCostDetails (data) {
    this.costDetailsTarget.innerHTML = this._decodeHtml(data.data.attributes.cost_details_html)
  }

  _updateProgramDetails (data) {
    this.programDetailsTarget.innerHTML = this._decodeHtml(data.data.attributes.program)
  }

  _updateCompanyKindWarningContent (data) {
    const baseHtml = data.data.attributes?.company_kind_warning_html

    if (!baseHtml) return

    this.companyKindWarningContentTarget.innerHTML = this._decodeHtml(baseHtml)
  }

  // Blank for a student or a company based abroad: the line hides.
  _updateUrssafWarningContent (data) {
    const baseHtml = data.data.attributes?.affiliated_to_urssaf_on_warning_html

    if (!baseHtml?.trim()) {
      this.urssafWarningTarget.classList.add("d-none")
      return
    }

    this.urssafWarningContentTarget.innerHTML = this._decodeHtml(baseHtml)
  }

  _updateNetworkAnimatorSelect (data) {
    const trainingSessionId = this.trainingSessionIdValue || this.trainingSessionIdTarget.value

    if (!trainingSessionId) return

    fetch(`/api/v1/trainee_assignments?training_session_id=${trainingSessionId}&trainee_id=${data.data.attributes.id}`, {
    })
      .then(response => response.json())
      .then(response => {
        this.networkAnimatorSelectTarget.value = response.data.attributes.profile_network_animator_id || this.currentUserProfileNetworkAnimatorIdValue
      }).catch(() => {
        // Back to default value
        this.networkAnimatorSelectTarget.value = this.currentUserProfileNetworkAnimatorIdValue
      })
  }

  _updateInvoiceFields (data) {
    this.invoiceFirstNameTargets.forEach((target) => { target.value = data.data.attributes.first_name })
    this.invoiceLastNameTargets.forEach((target) => { target.value = data.data.attributes.last_name })
    this.invoiceAddressTargets.forEach((target) => { target.value = data.data.attributes.company_address?.display_name })
    this.invoiceEmailTargets.forEach((target) => { target.value = data.data.attributes.email })

    this.financementModeFieldTargets.forEach(target => {
      target.dataset.enrollmentsFinancementModeTraineeFirstNameValue = data.data.attributes.first_name
      target.dataset.enrollmentsFinancementModeTraineeLastNameValue = data.data.attributes.last_name
      target.dataset.enrollmentsFinancementModeTraineeCompanyAddressDisplayNameValue = data.data.attributes.company_address?.display_name
      target.dataset.enrollmentsFinancementModeTraineeEmailValue = data.data.attributes.email
    })
  }

  _updateSubmitButton () {
    if (!this.hasSubmitButtonTarget) return

    const findOrCreateTraineeElement = document.getElementById(this.findOrCreateTraineeElementIdValue)
    if (!findOrCreateTraineeElement) return

    const traineeSelect = findOrCreateTraineeElement.querySelector("select[name='enrollment[profiles_trainee_id]']")
    if (!traineeSelect) return

    const traineeId = traineeSelect.value
    const costValue = this.hasCostTarget ? this.costTarget.value : ""
    const isZeroCost = this._isZeroCost()
    const financementModeAmountValue = this.hasFinancementModeAmountInputTarget ? this.financementModeAmountInputTarget.value : null
    const trainingIdNotFilledWhenPresent = this.hasTrainingIdTarget && !this.trainingIdTarget.value
    const trainingSessionIdNotFilledWhenPresent = this.hasTrainingSessionIdTarget && !this.trainingSessionIdTarget.value
    const financementModeRequired = !isZeroCost
    const costNotFilled = costValue === "" || Number.isNaN(this._parsedCost())

    if (!traineeId || costNotFilled || (financementModeRequired && !financementModeAmountValue) || trainingIdNotFilledWhenPresent || trainingSessionIdNotFilledWhenPresent) {
      this.submitButtonTarget.disabled = true
    } else {
      this.submitButtonTarget.disabled = false
    }
  }

  _decodeHtml (html) {
    if (typeof html !== "string") return ""

    return html.replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
  }
}
